import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'

interface Evidence {
  id: string
  title: string
  evidence_type: 'file' | 'link' | 'commit' | 'activity'
  path: string | null
  relationship: 'contributes' | 'completes'
  status: 'suggested' | 'confirmed' | 'rejected'
  added_by: { id: string; name: string } | null
  added_at: string | null
}

interface DeliverableBase {
  id: string
  title: string
  description?: string | null
  due_date: string | null
  status: 'not_started' | 'in_progress' | 'complete'
  verification?: string | null
  evidence?: Evidence[]
}

interface DeliverableCardProps {
  deliverable: DeliverableBase
  type: 'personal' | 'project'
  onUpdate?: () => void
  showAddEvidence?: boolean
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

function EvidenceTypeIcon({ type }: { type: string }) {
  const icons: Record<string, string> = {
    file: '📄',
    link: '🔗',
    commit: '💻',
    activity: '📋',
  }
  return <span>{icons[type] || '📎'}</span>
}

export function DeliverableCard({ deliverable: d, type, onUpdate, showAddEvidence = true }: DeliverableCardProps) {
  const [showEvidence, setShowEvidence] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newEvidence, setNewEvidence] = useState({ title: '', path: '', evidence_type: 'file' as const, relationship: 'contributes' as const })

  const queryClient = useQueryClient()
  const endpoint = type === 'personal' ? 'deliverables' : 'project-deliverables'

  const hasEvidence = (d.evidence?.length || 0) > 0

  // Add evidence mutation
  const addEvidenceMutation = useMutation({
    mutationFn: (data: typeof newEvidence) =>
      api.post(`/${endpoint}/${d.id}/evidence`, data),
    onSuccess: () => {
      queryClient.invalidateQueries()
      setShowAddForm(false)
      setNewEvidence({ title: '', path: '', evidence_type: 'file', relationship: 'contributes' })
      onUpdate?.()
    },
  })

  // Remove/unlink activity mutation
  const removeEvidenceMutation = useMutation({
    mutationFn: (evidenceId: string) =>
      api.delete(`/evidence/${evidenceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries()
      onUpdate?.()
    },
  })

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{d.title}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              d.status === 'complete' ? 'bg-green-900/50 text-green-300' :
              d.status === 'in_progress' ? 'bg-blue-900/50 text-blue-300' :
              'bg-gray-800 text-gray-300'
            }`}>
              {d.status === 'not_started' ? 'Not Started' :
               d.status === 'in_progress' ? 'In Progress' : 'Complete'}
            </span>
          </div>
          {d.description && (
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              {d.description}
            </p>
          )}
          <div className="flex gap-4 text-xs text-[var(--color-text-secondary)] mt-2">
            {d.due_date && <span>Due: {formatDate(d.due_date)}</span>}
            {d.verification && <span>Verification: {d.verification}</span>}
          </div>
        </div>
      </div>

      {/* Evidence Section */}
      {(hasEvidence || showAddEvidence) && (
        <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setShowEvidence(!showEvidence)}
              className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
            >
              {hasEvidence ? `${showEvidence ? 'Hide' : 'Show'} ${d.evidence?.length} linked activity item${d.evidence?.length !== 1 ? 's' : ''}` : 'No linked activity yet'}
            </button>
            {showAddEvidence && (
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="text-xs px-2 py-1 bg-[var(--color-surface)] rounded hover:bg-[var(--color-border)]"
              >
                + Add
              </button>
            )}
          </div>

          {/* Add Evidence Form */}
          {showAddForm && (
            <div className="mb-3 p-3 bg-[var(--color-surface)] rounded">
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Title (e.g., draft_report.pdf)"
                  value={newEvidence.title}
                  onChange={(e) => setNewEvidence(prev => ({ ...prev, title: e.target.value }))}
                  className="input w-full text-sm"
                />
                <input
                  type="text"
                  placeholder="Path or URL (optional)"
                  value={newEvidence.path}
                  onChange={(e) => setNewEvidence(prev => ({ ...prev, path: e.target.value }))}
                  className="input w-full text-sm"
                />
                <div className="flex gap-2">
                  <select
                    value={newEvidence.evidence_type}
                    onChange={(e) => setNewEvidence(prev => ({ ...prev, evidence_type: e.target.value as any }))}
                    className="input text-sm"
                  >
                    <option value="file">File</option>
                    <option value="link">Link</option>
                    <option value="commit">Commit</option>
                    <option value="activity">Activity</option>
                  </select>
                  <select
                    value={newEvidence.relationship}
                    onChange={(e) => setNewEvidence(prev => ({ ...prev, relationship: e.target.value as any }))}
                    className="input text-sm"
                  >
                    <option value="contributes">Contributes</option>
                    <option value="completes">Completes</option>
                  </select>
                  <button
                    onClick={() => addEvidenceMutation.mutate(newEvidence)}
                    disabled={!newEvidence.title || addEvidenceMutation.isPending}
                    className="btn-primary text-sm"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Evidence List */}
          {showEvidence && hasEvidence && (
            <div className="space-y-1">
              {d.evidence?.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-sm py-1 group">
                  <EvidenceTypeIcon type={e.evidence_type} />
                  <span className="truncate flex-1">{e.title}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    e.relationship === 'completes'
                      ? 'bg-green-900/50 text-green-300'
                      : 'bg-gray-800 text-gray-400'
                  }`}>
                    {e.relationship}
                  </span>
                  <button
                    onClick={() => removeEvidenceMutation.mutate(e.id)}
                    className="opacity-0 group-hover:opacity-100 text-xs px-1.5 py-0.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-opacity"
                    title="Unlink from deliverable"
                    disabled={removeEvidenceMutation.isPending}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DeliverableCard
